import { useCallback, useEffect, useMemo, useState } from "react";
import { getBilling } from "../../../api/ads";
import type { BillingItem, BillingListParams } from "../../../api/ads";

export const useBilling = (initialParams: BillingListParams = {}) => {
  const defaultParams: BillingListParams = useMemo(
    () => ({
      pageNum: 1,
      pageSize: 10,
      search: "",
      ...initialParams,
    }),
    [initialParams]
  );

  const [params, setParamsState] = useState<BillingListParams>(defaultParams);
  const [billing, setBilling] = useState<BillingItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchBilling = useCallback(
    async (override?: Partial<BillingListParams>) => {
      try {
        setListLoading(true);
        setError(null);
        const merged: BillingListParams = { ...params, ...override };
        const response = await getBilling(merged);
        setBilling(response.results);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
      } catch (err: any) {
        setError(err?.message || "Failed to load billing");
      } finally {
        setListLoading(false);
      }
    },
    [params]
  );

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const setParams = useCallback((next: Partial<BillingListParams>) => {
    setParamsState((prev) => ({ ...prev, ...next, pageNum: 1 }));
  }, []);

  const setPageNum = useCallback((pageNum: number) => {
    setParamsState((prev) => ({ ...prev, pageNum }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setParamsState((prev) => ({ ...prev, pageSize, pageNum: 1 }));
  }, []);

  const viewBillingHistory = useCallback(async (_id: string) => {
    setActionLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 150));
    } finally {
      setActionLoading(false);
    }
  }, []);

  return {
    functions: { fetchBilling, setParams, setPageNum, setPageSize, viewBillingHistory },
    values: { billing, params, totalCount, totalPages, error },
    loading: { listLoading, actionLoading },
  };
};

